import React, { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ItemBlock, ShowHide, ShowTask, Data, ShowSubTask, LineDiv, SubTaskList } from '../../styles';
import { TaskGroupHeader } from '../taskGroupHeader';
import TaskItem from '../../taskItem';
import {
  MobxFilteredTasksListInterface,
  NewTaskFilterInterface,
  SubtaskDataResult
} from '../../../../../interfaces/TaskInterface';
import { StatusListInterface } from '../../../../../interfaces/ProjectInterface';
import { TaskModel } from '../../../../../mobx/models/task';
import { WorkspaceModel } from '../../../../../mobx/models/workspace';
import { SHOW_TASK_GROUPING_TYPE } from '../../../../../global/constants';
import type { ProjectModel } from '../../../../../mobx/models/project';
import type { ProjectTemplateModel } from '../../../../../mobx/models/projectTemplate';

export interface TaskGroupModalActions {
  setOpen: (value: boolean) => void;
  setDeleteModalOpen: (value: boolean) => void;
  setSelectedItem: (value: TaskModel) => void;
}

interface SortableTaskItemProps {
  task: TaskModel;
  render: (dragHandleProps: Record<string, unknown>) => React.ReactNode;
}

const SortableTaskItem: React.FC<SortableTaskItemProps> = ({ task, render }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    border: '0.5px solid transparent'
  };
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div ref={setNodeRef} style={style} {...attributes}>
      {render({ ...listeners })}
    </div>
  );
};

interface TaskGroupProps {
  item: MobxFilteredTasksListInterface;
  collapseIds: string[];
  handleCollapseClick: (id: string) => void;
  loadData: () => void;
  currentWorkspace: WorkspaceModel | null;
  taskFilter: NewTaskFilterInterface | undefined;
  isTemplateProject: boolean | undefined;
  currentProject: ProjectModel | ProjectTemplateModel | null;
  isView: boolean | undefined;
  showProjectName: boolean | undefined;
  selectedId: string;
  showCompletedTask: boolean;
  openSubTask: { id: string; showCompleted: boolean };
  isSelected: (groupId: string, taskId: string) => boolean;
  handleHoverTask: (groupId: string, taskId: string, isCompleted?: boolean) => void;
  onClickTaskCheckBox: (task: TaskModel) => void;
  onChangeStatus: (task: TaskModel, status: StatusListInterface, previousStatus: StatusListInterface) => void;
  handleSubtaskOpen: (id: string) => void;
  handleShowCompletedSubTask: (id: string, isShow: boolean) => void;
  toggleCompletedTask: (show: boolean, comparedValue: string) => void;
  getSubtaskData: (task: TaskModel) => SubtaskDataResult;
  modalActions: TaskGroupModalActions;
  maxVisibleLabels: number;
}

const TaskGroup = observer(function TaskGroup(props: TaskGroupProps) {
  const {
    item,
    collapseIds,
    handleCollapseClick,
    loadData,
    currentWorkspace,
    taskFilter,
    isTemplateProject,
    currentProject,
    isView,
    showProjectName,
    selectedId,
    showCompletedTask,
    openSubTask,
    isSelected,
    handleHoverTask,
    onClickTaskCheckBox,
    onChangeStatus,
    handleSubtaskOpen,
    handleShowCompletedSubTask,
    toggleCompletedTask,
    getSubtaskData,
    modalActions,
    maxVisibleLabels
  } = props;

  const { setOpen, setDeleteModalOpen, setSelectedItem } = modalActions;

  const groupingStatusValue = taskFilter?.groupingStatus ?? SHOW_TASK_GROUPING_TYPE.NO_GROUPING;

  const groupId = groupingStatusValue === SHOW_TASK_GROUPING_TYPE.NO_GROUPING ? 'no_grouping' : String(item?.id || '');

  // Determine compared value for toggle logic
  let comparedValue: string;
  if (
    groupingStatusValue === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_USER ||
    groupingStatusValue === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_PRIORITY
  ) {
    comparedValue = item?.name || '';
  } else {
    comparedValue = `${item?.id || ''}`;
  }

  const handleToggleCompleted = useCallback(
    (show: boolean) => toggleCompletedTask(show, show ? comparedValue : ''),
    [toggleCompletedTask, comparedValue]
  );

  const handleShowCompleted = useCallback(() => handleToggleCompleted(true), [handleToggleCompleted]);
  const handleHideCompleted = useCallback(() => handleToggleCompleted(false), [handleToggleCompleted]);

  const handleShowSubtaskCompletedToggle = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const taskId = event.currentTarget.dataset.taskId || '';
      const showCompleted = event.currentTarget.dataset.showCompleted === 'true';

      handleShowCompletedSubTask(taskId, showCompleted);
    },
    [handleShowCompletedSubTask]
  );

  const taskIds = useMemo(() => (item?.tasks || []).map((t) => t.id), [item?.tasks]);
  const { setNodeRef } = useDroppable({ id: String(item.id) });

  // Check if group should be rendered
  if (
    !(
      (item?.tasks && item?.tasks?.length > 0) ||
      (item?.completedTasks && item?.completedTasks?.length > 0) ||
      taskFilter?.showEmptyGroup ||
      isTemplateProject
    )
  ) {
    return <React.Fragment />;
  }

  return (
    <ItemBlock ref={setNodeRef}>
      <TaskGroupHeader
        item={item}
        taskFilterGroupingStatus={taskFilter?.groupingStatus}
        isTemplateProject={isTemplateProject || false}
        loadData={loadData}
        currentWorkspace={currentWorkspace}
        collapseIds={collapseIds}
        handleCollapseClick={handleCollapseClick}
      />

      <SortableContext id={String(item.id)} items={taskIds} strategy={verticalListSortingStrategy}>
        {/* Active Tasks */}
        {item?.tasks?.map((task: TaskModel) => {
          if (collapseIds.includes(String(item.id))) return <React.Fragment key={task?.id} />;
          const { visibleSubtasks, completedSubtasks } = getSubtaskData(task);
          return (
            <SortableTaskItem
              task={task}
              key={task?.id}
              render={(dragHandleProps) => (
                <>
                  <TaskItem
                    currentProject={currentProject!}
                    isSelected={isSelected(groupId, task?.id || '')}
                    loadData={loadData}
                    onClickTaskCheckBox={onClickTaskCheckBox}
                    onChangeStatus={onChangeStatus}
                    handleHoverTask={handleHoverTask}
                    groupId={`${item?.id || ''}`}
                    dragHandleProps={dragHandleProps}
                    task={task}
                    isTemplateProject={isTemplateProject}
                    setOpen={setOpen}
                    setDeleteModelopen={setDeleteModalOpen}
                    setSelectedItem={setSelectedItem}
                    handleSubtaskOpen={handleSubtaskOpen}
                    isActiveSubTask={openSubTask?.id === task?.id}
                    isView={isView}
                    showProjectName={showProjectName}
                    maxVisibleLabels={maxVisibleLabels}
                  />

                  {/* Subtasks */}
                  {task?.subTasks &&
                    task?.subTasks?.length > 0 &&
                    ((taskFilter?.showSubTask && !isView) || openSubTask?.id === task?.id) && (
                      <ShowSubTask>
                        <LineDiv isApplyMargin={completedSubtasks && completedSubtasks?.length > 0} />
                        <SubTaskList>
                          {visibleSubtasks?.map((sub) => {
                            const subTaskItem: TaskModel = sub;
                            return (
                              <TaskItem
                                key={subTaskItem?.id}
                                currentProject={subTaskItem?.project!}
                                isSelected={isSelected(groupId, subTaskItem?.id || '')}
                                loadData={loadData}
                                onClickTaskCheckBox={onClickTaskCheckBox}
                                onChangeStatus={onChangeStatus}
                                handleHoverTask={handleHoverTask}
                                groupId={`${item?.id || ''}`}
                                task={subTaskItem}
                                isTemplateProject={isTemplateProject}
                                setOpen={setOpen}
                                setDeleteModelopen={setDeleteModalOpen}
                                setSelectedItem={setSelectedItem}
                                isSubTask={true}
                                isView={isView}
                                showProjectName={showProjectName}
                                maxVisibleLabels={maxVisibleLabels}
                              />
                            );
                          })}

                          {/* Show Completed Subtasks Toggle */}
                          {completedSubtasks && completedSubtasks?.length > 0 && (
                            <>
                              {(!openSubTask?.showCompleted || openSubTask?.id !== task?.id) && (
                                <ShowHide
                                  role='button'
                                  showSubTask={true}
                                  data-task-id={task?.id}
                                  data-show-completed='true'
                                  onClick={handleShowSubtaskCompletedToggle}>
                                  Show Completed Tasks
                                </ShowHide>
                              )}
                              {openSubTask?.showCompleted && openSubTask?.id === task?.id && (
                                <ShowHide
                                  role='button'
                                  showSubTask={true}
                                  data-task-id={task?.id}
                                  data-show-completed='false'
                                  onClick={handleShowSubtaskCompletedToggle}>
                                  Hide Completed Tasks
                                </ShowHide>
                              )}
                            </>
                          )}
                        </SubTaskList>
                      </ShowSubTask>
                    )}
                </>
              )}
            />
          );
        })}
      </SortableContext>

      {/* Completed Tasks */}
      {item?.completedTasks && item?.completedTasks?.length > 0 && (
        <ShowTask>
          {selectedId === comparedValue && showCompletedTask ? (
            <ShowHide role='button' onClick={handleHideCompleted}>
              Hide Completed Tasks
            </ShowHide>
          ) : (
            <ShowHide role='button' onClick={handleShowCompleted}>
              Show Completed Tasks
            </ShowHide>
          )}

          {selectedId === comparedValue && showCompletedTask && item?.completedTasks?.length > 0 && (
            <Data isShowCompletedTask={true}>
              {item?.completedTasks?.map((task: TaskModel) => {
                const { visibleSubtasks, completedSubtasks } = getSubtaskData(task);
                return (
                  <React.Fragment key={task.id}>
                    <TaskItem
                      currentProject={currentProject!}
                      isSelected={isSelected(groupId, task?.id || '')}
                      loadData={loadData}
                      onClickTaskCheckBox={onClickTaskCheckBox}
                      onChangeStatus={onChangeStatus}
                      handleHoverTask={handleHoverTask}
                      groupId={`${item?.id || ''}`}
                      task={task}
                      isCompletedTask
                      isTemplateProject={isTemplateProject}
                      setOpen={setOpen}
                      setDeleteModelopen={setDeleteModalOpen}
                      setSelectedItem={setSelectedItem}
                      handleSubtaskOpen={handleSubtaskOpen}
                      isActiveSubTask={openSubTask?.id === task?.id}
                      isView={isView}
                      showProjectName={showProjectName}
                      maxVisibleLabels={maxVisibleLabels}
                    />

                    {/* Subtasks for Completed Tasks */}
                    {task?.subTasks &&
                      task?.subTasks?.length > 0 &&
                      ((taskFilter?.showSubTask && !isView) || openSubTask?.id === task?.id) && (
                        <ShowSubTask>
                          <LineDiv isApplyMargin={completedSubtasks && completedSubtasks?.length > 0} />
                          <SubTaskList>
                            {visibleSubtasks?.map((sub) => {
                              const subTaskItem: TaskModel = sub;
                              return (
                                <TaskItem
                                  key={subTaskItem?.id}
                                  isSubTask={true}
                                  currentProject={isTemplateProject ? currentProject! : subTaskItem?.project!}
                                  isSelected={isSelected(groupId, subTaskItem?.id || '')}
                                  loadData={loadData}
                                  onClickTaskCheckBox={onClickTaskCheckBox}
                                  onChangeStatus={onChangeStatus}
                                  handleHoverTask={handleHoverTask}
                                  groupId={`${item?.id || ''}`}
                                  task={subTaskItem}
                                  isCompletedTask
                                  isTemplateProject={isTemplateProject}
                                  setOpen={setOpen}
                                  setDeleteModelopen={setDeleteModalOpen}
                                  setSelectedItem={setSelectedItem}
                                  isView={isView}
                                  showProjectName={showProjectName}
                                  maxVisibleLabels={maxVisibleLabels}
                                />
                              );
                            })}

                            {/* Show Completed Subtasks Toggle for Completed Tasks */}
                            {completedSubtasks && completedSubtasks?.length > 0 && (
                              <>
                                {(!openSubTask?.showCompleted || openSubTask?.id !== task?.id) && (
                                  <ShowHide
                                    role='button'
                                    showSubTask={true}
                                    data-task-id={task?.id}
                                    data-show-completed='true'
                                    onClick={handleShowSubtaskCompletedToggle}>
                                    Show Completed Tasks
                                  </ShowHide>
                                )}
                                {openSubTask?.showCompleted && openSubTask?.id === task?.id && (
                                  <ShowHide
                                    role='button'
                                    showSubTask={true}
                                    data-task-id={task?.id}
                                    data-show-completed='false'
                                    onClick={handleShowSubtaskCompletedToggle}>
                                    Hide Completed Tasks
                                  </ShowHide>
                                )}
                              </>
                            )}
                          </SubTaskList>
                        </ShowSubTask>
                      )}
                  </React.Fragment>
                );
              })}
            </Data>
          )}
        </ShowTask>
      )}
    </ItemBlock>
  );
});

export default TaskGroup;
